import React from 'react';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';

import { routePathFavorites } from 'src/constants/router';
import { colours } from 'src/constants/colours';

import useAppRouter from 'src/hooks/useAppRouter';
import useLoginModal from 'src/hooks/useLoginModal';

import type { Medium } from 'src/types/__generated__/graphql';

import { styledComponent, styledTag } from 'src/utils/styled';
import type { GalleryCollaborator } from 'src/components/favorites/CollaboratorsAvatarList';
import { CollaboratorsAvatarList } from 'src/components/favorites/CollaboratorsAvatarList';
import Tooltip from 'src/components/ui/Tooltip';
import { GalleryPreviewDataInfos } from 'src/components/favorites/GalleryPreview/view/GalleryPreviewDataInfos';
import { GalleryPreviewImage } from 'src/components/favorites/GalleryPreview/view/GalleryPreviewImage';

const StyledCard = styledComponent(Card)`
    box-shadow: none;
    cursor: pointer;
    max-height: 16.875rem;
`;

const StyledCardActionArea = styledComponent(CardActionArea)`
    display: flex;
    flex-direction: column;
`;

const LockIconWrapper = styledTag('div')`
    background-color: ${colours.white.main};
    border-radius: 50%;
    opacity: 0.6;
`;

type PreviewMedia = Array<{
    type: Medium['type'];
    url: Medium['url'];
    isTranscoding?: Medium['isTranscoding'];
} | null>;

type GalleryData = {
    id: string;
    name: string;
    numberOfContents: number;
    previewMedia?: PreviewMedia | null;
    isSecret: boolean;
    contentLastUpdatedAt?: Date;
    collaborators?: {
        items: GalleryCollaborator[];
        totalCount: number;
    };
};

interface Props {
    gallery: GalleryData;
    showGalleryDataInfos?: boolean;
    showCollaborators?: boolean;
}

export const GalleryPreviewCard: React.FC<Props> = ({
    gallery,
    showGalleryDataInfos = true,
    showCollaborators = true,
}) => {
    const { t } = useTranslation(['favorites']);
    const router = useAppRouter();
    const { createAuthenticatedHandler } = useLoginModal();

    const handleCardClick = async () => {
        await createAuthenticatedHandler(async () => {
            await router.push(`${routePathFavorites}/${gallery.id}`);
        });
    };

    return (
        <StyledCard>
            <StyledCardActionArea onClick={handleCardClick}>
                {gallery.isSecret && (
                    <Tooltip
                        title={t('favorites:privateGallery')}
                        containerStyle={{ zIndex: 1, position: 'absolute', top: '0.5rem', left: '0.5rem' }}
                    >
                        <LockIconWrapper>
                            <Icon name="Lock" size="1.5rem" color={colours.black.main} />
                        </LockIconWrapper>
                    </Tooltip>
                )}
                <GalleryPreviewImage previewMedia={gallery.previewMedia} showShowcaseGrid />
                <Box display="flex" justifyContent="space-between" width="100%">
                    <Box padding="0.3rem 0.6rem" display="flex" flexDirection="column" flex={1} minWidth={0}>
                        <Typography
                            marginBottom="0.1rem"
                            data-target-id={`favorite-card-title-id-${gallery.id}`}
                            variant="h4"
                            noWrap
                        >
                            {gallery.name}
                        </Typography>
                        {showGalleryDataInfos && <GalleryPreviewDataInfos gallery={gallery} />}
                    </Box>
                    {showCollaborators && gallery.collaborators?.items.length && (
                        <Box marginTop="0.4rem" mx="0.2rem">
                            <CollaboratorsAvatarList
                                collaborators={gallery.collaborators.items}
                                additionalCollaboratorsCount={
                                    gallery.collaborators.totalCount - gallery.collaborators.items.length
                                }
                            />
                        </Box>
                    )}
                </Box>
            </StyledCardActionArea>
        </StyledCard>
    );
};
